import { Dialog } from '@angular/cdk/dialog';
import { Overlay } from '@angular/cdk/overlay';
import type { ComponentType } from '@angular/cdk/portal';
import { Injectable, Injector } from '@angular/core';
import type { TemplateRef } from '@angular/core';
import { take } from 'rxjs';
import { generateRandomId } from '../../shared/others/utils';
import { PalantirModalAnimatedContainerComponent } from './components/modal-animated-container/modal-animated-container.component';
import { modalLogger } from './logger.instance';
import { PalantirModalRef } from './modal-ref.service';
import type { PalantirModalOptions } from './modal.type';

const logger = modalLogger.extend('service');

@Injectable({
  providedIn: 'root',
})
export class PalantirModalService {
  public constructor(
    private readonly dialog: Dialog,
    private readonly overlay: Overlay,
    private readonly injector: Injector,
  ) {}

  public openModal<T extends ComponentType<unknown> | TemplateRef<unknown>>(
    component: T,
    options?: PalantirModalOptions<T>,
  ): PalantirModalRef {
    const size = this.getSize(options?.size);

    const palantirModalRef = new PalantirModalRef(this.injector);

    const debugId = logger.enabled('debug') ? generateRandomId() : null;

    logger.debug('Modal opened (DebugId: ' + debugId + ')', options, size);

    if (options?.data && typeof options?.data !== 'object') {
      logger.warn(
        'Modal data is not an object, it will be spread and may cause unexpected behavior',
        options?.data,
      );
    }

    const dialogRef = this.dialog.open(component, {
      panelClass: 'palantir-modal-container',
      width: size.width,
      maxWidth: size.maxWidth,
      height: '100%',
      autoFocus: options?.autoFocus ?? false,
      positionStrategy: this.overlay.position().global().end().top(),
      data: {
        ...options?.data,

        // eslint-disable-next-line @typescript-eslint/unbound-method
        canClose: options?.canClose,
        confirmation: options?.confirmation,
      },
      disableClose: true,
      providers: (dialogRef) => {
        palantirModalRef.dialogRef = dialogRef;

        return [
          {
            provide: PalantirModalRef,
            useValue: palantirModalRef,
          },
        ];
      },
      container: PalantirModalAnimatedContainerComponent,
    });

    palantirModalRef.dialogRef = dialogRef;

    if (logger.enabled('debug')) {
      dialogRef.closed.pipe(take(1)).subscribe(() => {
        logger.debug('Modal closed');
      });
    }

    return palantirModalRef;
  }

  private getSize(size?: PalantirModalOptions['size']): { maxWidth: string; width: string } {
    const maxWidth = 'calc(100% - 240px)';

    const widthBySize = {
      '2x-large': '1600px',
      'x-large': '1400px',
      large: '1200px',
      medium: '860px',
      small: '400px',
    };

    return {
      width: widthBySize[size ?? 'medium'],
      maxWidth,
    };
  }
}
